import {
  Fragment,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import {
  Job,
  jobsService,
  EngagePostMessageActions,
  ImagesService,
  SendMessage,
  useIframeEngageActionListener,
} from '../services';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import {
  AddPhotosButton,
  SearchBoxHeader,
  SelectJobDialogContent,
} from './save-to-job-modal.styles';

interface SaveToJobModalProps {
  accessToken: string;
  api1Origin: string;
  engageOrigin: string;
}

export const SaveToJobModal: FunctionComponent<SaveToJobModalProps> = ({
  accessToken,
  api1Origin,
  engageOrigin,
}) => {
  const eventBody = useIframeEngageActionListener({
    engageOrigin,
    action: EngagePostMessageActions.OPEN_SAVE_TO_JOB_MODAL,
  });
  const [addPhotosDisabled, setAddPhotosDisabled] = useState(true);
  const [contactId, setContactId] = useState('');
  const [jobId, setJobId] = useState('');
  const [open, setOpen] = useState(false);
  const [openAutocomplete, setOpenAutocomplete] = useState(false);
  const [options, setOptions] = useState<readonly Job[]>([]);
  const [optionsExist, setOptionsExist] = useState(true);
  const [photoUrl, setPhotoUrl] = useState('');
  const loading = useMemo(
    () => openAutocomplete && optionsExist && options.length === 0,
    [openAutocomplete, options, optionsExist]
  );

  const onChangeJobId = (e, newValue) => {
    const id = newValue.jobId;
    setJobId(id);
    setAddPhotosDisabled(false);
  };

  useEffect(() => {
    setOpen(eventBody != null);

    if (eventBody) {
      if (eventBody.associationType === 'contact') {
        setContactId(eventBody.associationId);
      }
      setPhotoUrl(eventBody.photoUrl);
    }
  }, [eventBody]);

  useEffect(() => {
    const setJobsByContactId = async () => {
      const response = await jobsService.getJobsByContactId(
        engageOrigin,
        accessToken,
        contactId
      );

      setOptionsExist(response.length > 0);
      setOptions(response);
    };

    let active = true;

    if (!loading) {
      return;
    }

    if (active) {
      setJobsByContactId();
    }

    return () => {
      active = false;
    };
  }, [loading, contactId, accessToken, engageOrigin]);

  useEffect(() => {
    if (!openAutocomplete) {
      setOptions([]);
    }
  }, [openAutocomplete]);

  const handleClose = () => {
    setOpen(false);
    setAddPhotosDisabled(true);
    setOpenAutocomplete(false);
    setOptions([]);
    setJobId('');
    setContactId('');
    setOptionsExist(true);
  };

  const handleSavePhoto = async () => {
    const imagesService = new ImagesService(
      engageOrigin,
      api1Origin,
      accessToken
    );
    const media = await imagesService.savePhotoByUrlToJob(jobId, photoUrl);
    const payload = { media };

    SendMessage(payload, engageOrigin, true);
    handleClose();
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="select-job-title"
        aria-describedby="select-job-description"
      >
        <DialogTitle id="select-job-title">{'Save Photo to Job'}</DialogTitle>
        <DialogContent>
          <img
            src={photoUrl}
            style={{ height: '200px', objectFit: 'contain', width: '200px' }}
          />
          <SelectJobDialogContent>
            <SearchBoxHeader>Search job</SearchBoxHeader>
            <Autocomplete
              fullWidth
              disablePortal={true}
              options={options}
              open={openAutocomplete}
              isOptionEqualToValue={(option, value) =>
                option.name === value.name
              }
              getOptionLabel={(option) => (option as Job).name}
              loading={loading}
              onChange={onChangeJobId}
              data-e2e="search-jobs-autocomplete"
              onOpen={() => {
                setOpenAutocomplete(true);
              }}
              onClose={() => {
                setOpenAutocomplete(false);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="filled"
                  label="Enter name or search job"
                  data-e2e="search-jobs"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
            />
          </SelectJobDialogContent>
        </DialogContent>
        <DialogActions>
          <Button data-e2e="cancel-upload-job-photo" onClick={handleClose}>
            Cancel
          </Button>
          {/* Intentionally disabled but kept to align with the modal flow */}
          <AddPhotosButton
            disabled={addPhotosDisabled}
            data-e2e="add-job-photo"
            onClick={handleSavePhoto}
          >
            Save Photo
          </AddPhotosButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};
